import { MultipleFileFieldControl } from '@anecdotes/design-system/form-controls';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFormGroup } from '@anecdotes/dynamic-form';
import { filesLengthValidator, filesWeightValidator, filesRequired } from './custom-validators';
import { ModalWindowService } from '../../services/modal-window/modal-window.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-upload-files-modal',
  templateUrl: './upload-files-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UploadFilesModalComponent implements OnInit {
  readonly isLoading$ = new BehaviorSubject<boolean>(false);

  @Input({ required: true })
  rootTranslationKey: string;

  @Input({ required: true })
  confimationActionFn: (files: File[]) => void | Promise<void>;

  @Input()
  maxFilesCount = 25;

  @Input()
  maxFilesWeight = 30;

  @Input()
  tipTemplate: TemplateRef<HTMLElement>;

  form: DynamicFormGroup<{ file: MultipleFileFieldControl }>;

  get isFormValid(): boolean {
    return !this.form.items?.file?.errors;
  }

  constructor(
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private modalWindowService: ModalWindowService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initializeFormListener();
  }

  buildTranslationKey(relativeKey: string): string {
    return `${this.rootTranslationKey}.${relativeKey}`;
  }

  async handleCTAClick(): Promise<void> {
    if (this.form?.value?.file) {
      this.isLoading$.next(true);
      await this.confimationActionFn(Array.from(this.form.value.file));
      this.isLoading$.next(false);
      this.modalWindowService.close();
    }
  }

  private initializeFormListener(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isFormValid && this.form.value.file) {
        this.form.controls.file.markAsDirty();
      }
      this.cd.detectChanges();
    });
  }

  private initForm(): void {
    this.form = new DynamicFormGroup({
      file: new MultipleFileFieldControl({
        initialInputs: {
          label: this.translateService.instant(this.buildTranslationKey('attachFileInputTitle')),
          validateOnDirty: true,
          required: true,
          labelIcon: 'upload-file',
          errorTexts: {
            fileLengthError: this.translateService.instant(this.buildTranslationKey('fileLengthError'), { count: this.maxFilesCount }),
            filesWeightError: this.translateService.instant(this.buildTranslationKey('fileWeightError'), { maxSize: this.maxFilesWeight }),
          },
        },
        validators: [
          Validators.required,
          filesRequired(),
          filesLengthValidator(this.maxFilesCount),
          filesWeightValidator(this.maxFilesWeight),
        ],
      }),
    });
  }
}
